'use client';
import React, { FC } from 'react';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';
import { TypeValuePropComponentRowFields } from '@/core/generated-types';
import { DynamicComponent } from '@/components';
import { BackgroundColorWrapper } from '@/components/common/BackgroundColorWrapper';
import { UniversalCta } from '../UniversalCta';
import classNames from 'classnames';
import Slider, { Settings } from 'react-slick';
import { Arrow } from '../Carousel/Arrows';

function getItemClasses(numItems = 0) {
  let widthClass = 'w-full';
  if (numItems === 2) {
    widthClass = 'w-full md:w-1/2';
  } else if (numItems === 3 || numItems > 4) {
    widthClass = 'w-full md:w-1/3';
  } else if (numItems === 4) {
    widthClass = 'w-full md:w-1/4';
  }

  return `flex-none md:px-6 ${widthClass}`;
}

const ValuePropComponentRow: FC<TypeValuePropComponentRowFields> = (props) => {
  const {
    anchorId,
    backgroundColor,
    components,
    cta,
    display = 'row',
    entryName,
    maxWidth = '1400px',
    subtitle,
    title,
    trailingText,
  } = props;

  const settings: Settings = {
    nextArrow: <Arrow direction="right" />,
    prevArrow: <Arrow direction="left" />,
    slidesToScroll: 1,
    dots: true,
    className: 'luv-carousel',
    slidesToShow: 1,
    swipeToSlide: true,
    arrows: true,
    infinite: true,
  };

  return (
    <BackgroundColorWrapper
      color={backgroundColor?.fields?.color}
      classes="py-16"
    >
      <section
        className="mx-auto px-8"
        data-testid={`text-block-${entryName}`}
        id={anchorId}
        style={{ maxWidth }}
      >
        <div className="font-bold text-center text-2xl md:text-[40px] md:leading-[48px]">
          {title}
        </div>
        {subtitle && (
          <div className="mx-auto max-w-[650px] pt-6 text-center">
            {subtitle}
          </div>
        )}

        <div
          className={classNames(
            'mt-8 flex flex-wrap justify-center gap-y-6 md:mt-12 md:gap-y-12 md:gap-y-16',
            { 'hidden md:flex': display === 'carousel' },
            // getContainerClasses(components?.length),
          )}
        >
          {components?.map((component, i) => (
            <div
              key={`${component.sys.id}-${i}`}
              className={getItemClasses(components?.length)}
            >
              <DynamicComponent component={component} display={display} />
            </div>
          ))}
        </div>

        <div
          className={classNames('slider-variant-valuePropRow mt-8', {
            hidden: display !== 'carousel',
            'block md:hidden': display === 'carousel',
          })}
        >
          <Slider {...settings}>
            {components?.map((slide, i) => {
              return (
                // The following styles ensure slides are centered in the slider
                <div
                  key={`${slide.sys.id}-${i}`}
                  className={classNames('!flex h-full justify-center px-8')}
                >
                  <DynamicComponent component={slide} display={display} />
                </div>
              );
            })}
          </Slider>
        </div>

        {cta && (
          <div className="flex flex-col items-center justify-center pt-8 md:pt-12">
            <UniversalCta
              link={cta}
              defaultButtonStyle="Primary"
              className="inline-flex"
            />
            {trailingText && (
              <div className="mt-4 text-center text-d-sm">{trailingText}</div>
            )}
          </div>
        )}
      </section>
    </BackgroundColorWrapper>
  );
};

export default React.memo(ValuePropComponentRow);
